// src/DatDetailPage.jsx
import React, { useMemo, useState, useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AppContext } from './AppContext';

const toMoneyNumber = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = Number(String(v).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export default function DatDetailPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: routeId } = useParams();

  const {
    assetsSummary,
    userWallet,
    userId2,
  } = useContext(AppContext);

  // Resolve the DAT either from router state or from assetsSummary by id
  const dat = useMemo(() => {
    const fromState = state?.dat;
    if (fromState) return fromState;
    if (!assetsSummary?.length) return null;
    return assetsSummary.find(a => String(a.id) === String(routeId)) || null;
  }, [state, assetsSummary, routeId]);

  const matchingWallet = useMemo(() => {
    return (userWallet?.ownedDats || []).filter(w => w.id === dat?.id);
  }, [userWallet, dat]);

  // Merge wallet fields (earnings/sold/isForSale) onto the editions the user owns
  const ownedEditions = useMemo(() => {
    const base = Array.isArray(dat?.ownedEditions) ? dat.ownedEditions : [];
    return base.map(ed => {
      const w = matchingWallet.find(m => m.edition === ed.edition);
      return {
        ...ed,
        earnings: w?.earnings ?? 0,
        sold: w?.sold ?? true,
        isForSale: w?.isForSale ?? ed.isForSale ?? false,
      };
    });
  }, [dat, matchingWallet]);

  const totalOwnedEarnings = useMemo(
    () => ownedEditions.reduce((s, ed) => s + toMoneyNumber(ed.earnings), 0),
    [ownedEditions]
  );

  // ----- Sell flow -----
  const [sellOpen, setSellOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const openSell = (edition) => {
    setSelectedEdition(edition);
    setErrorMsg('');
    const oldPrice = Number(edition?.price || 0);
    const suggested = oldPrice + oldPrice * 0.8;
    setPriceInput(String(Number.isFinite(suggested) ? suggested.toFixed(2) : ''));
    setSellOpen(true);
  };

  const handleContinue = () => {
    setSellOpen(false);
    setConfirmOpen(true);
  };

  const enteredPrice = Number(priceInput) || 0;

  const confirmSell = async () => {
    if (!dat || !selectedEdition) return;
    const oldPrice = Number(selectedEdition?.price || 0);
    if (enteredPrice < oldPrice) {
      setErrorMsg(`Price must be at least $${oldPrice.toFixed(2)}`);
      setConfirmOpen(false);
      setSellOpen(true);
      return;
    }
    try {
      setBusy(true);
      const fn = httpsCallable(getFunctions(), 'setEditionForSaleCF');
      await fn({
        datId: dat.id,
        editionNumber: selectedEdition.edition,
        newPrice: enteredPrice,
      });
      // Optimistic UI: mark this edition as for sale
      if (state?.dat) {
        state.dat.ownedEditions = (state.dat.ownedEditions || []).map(ed =>
          ed.edition === selectedEdition.edition ? { ...ed, isForSale: true } : ed
        );
      }
      alert(`Your DAT (Edition #${selectedEdition.edition}) is now for sale at $${enteredPrice.toFixed(2)}!`);
    } catch (e) {
      console.error('setEditionForSaleCF failed', e);
      alert('Failed to list DAT for sale. Please try again.');
    } finally {
      setBusy(false);
      setConfirmOpen(false);
      setSellOpen(false);
    }
  };

  if (!dat) {
    return (
      <div className="dat-detail-wrap">
        <div className="dc-card">
          <div className="dc-note">No DAT selected.</div>
        </div>
        {styles}
      </div>
    );
  }

  const canSellFromThisPage = dat?.SoldOut === true;

  const goAnalytics = () => {
    navigate('/analytics', { state: { assetId: dat.id, songName: dat.SongName } });
  };

  return (
    <div className="dat-detail-wrap">
      <div className="dc-card dat-header">
        <button className="dc-btn ghost small" onClick={() => navigate(-1)}>← Back</button>
        <div className="dat-title">
          <div className="dat-song">‘{dat.SongName}’</div>
          <div className="dat-artist">@{dat.creatorUsername || dat.ArtistName}</div>
        </div>
        <div className="dat-cta">
          <button className="dc-btn small" onClick={goAnalytics}>View Analytics</button>
        </div>
      </div>

      {/* hero */}
      <div className="dc-card dat-hero">
        <img className="dat-art" src={dat.ArtworkURL} alt={`Artwork for ${dat.SongName}`} />
        <div className="dat-meta">
          <div className="meta-row">
            <span className="meta-label">Owned Editions</span>
            <span className="meta-value">{ownedEditions.length}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">Total Earnings</span>
            <span className="meta-value">${totalOwnedEarnings.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">Sold Out</span>
            <span className="meta-value">{dat.SoldOut ? 'Yes' : 'No'}</span>
          </div>
        </div>
      </div>

      {/* editions list */}
      <div className="dc-card">
        <div className="list-title">Your Editions</div>
        {ownedEditions.length === 0 ? (
          <div className="dc-note">You don’t own any editions of this DAT.</div>
        ) : (
          <ul className="ed-list">
            {ownedEditions.map((ed) => (
              <li key={ed.edition} className="ed-item">
                <div className="ed-left">
                  <div className="ed-line">
                    <span className="ed-label">Edition</span>
                    <span className="ed-value">#{ed.edition}</span>
                  </div>
                  {dat.creatorId !== userId2 && (
                    <div className="ed-line">
                      <span className="ed-label">Royalties</span>
                      <span className="ed-value">{(ed.royalties || 0).toFixed(2)}%</span>
                    </div>
                  )}
                  <div className="ed-line">
                    <span className="ed-label">Earnings</span>
                    <span className="ed-value">
                      ${toMoneyNumber(ed.earnings).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
                    </span>
                  </div>
                </div>

<div className="ed-right">
  {ed.sold ? (
    <span className="chip solid">Sold</span>
  ) : ed.isForSale ? (
    <span className="chip solid">For Sale</span>
  ) : canSellFromThisPage ? (
    <button className="dc-btn small" onClick={() => openSell(ed)}>Sell DAT</button>
  ) : null}
</div>

              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Sell modal */}
      {sellOpen && (
        <div className="modal-overlay" onClick={() => setSellOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">List Edition #{selectedEdition?.edition}</h3>

            <div className="form-row">
              <label className="form-label">Suggested Sale Price</label>
              <div className="form-value">${Number(priceInput || 0).toFixed(2)}</div>
            </div>

            <div className="form-row">
              <label className="form-label">Enter Custom Sale Price</label>
              <input
                className="text-input"
                inputMode="decimal"
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                placeholder="0.00"
              />
            </div>

            {errorMsg ? <div className="error">{errorMsg}</div> : null}

            <div className="modal-actions">
              <button className="dc-btn full" onClick={handleContinue}>Continue</button>
              <button className="dc-btn ghost full" onClick={() => setSellOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="modal-overlay" onClick={() => setConfirmOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Confirm Sale</h3>
            <p className="confirm-copy">
              You’re about to list edition #{selectedEdition?.edition} of “{dat.SongName}” for ${enteredPrice.toFixed(2)}.
            </p>
            <div className="modal-actions">
              <button disabled={busy} className="dc-btn full" onClick={confirmSell}>
                {busy ? 'Listing…' : 'Confirm'}
              </button>
              <button className="dc-btn ghost full" onClick={() => setConfirmOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {styles}
    </div>
  );
}

/* ───── styles (DAT Chain theme) ───── */
const styles = (
  <style>{`
  :root{
    --brand-top:#fe24ad;
    --brand-bot:#a410e8;
    --brand-grad:linear-gradient(135deg,var(--brand-top),var(--brand-bot));
    --brand-top-20:rgba(254,36,173,.2);
    --brand-bot-40:rgba(164,16,232,.4);
  }

    *{margin:0;padding:0;box-sizing:border-box;}
  html,body{font-family:sans-serif;background:#000;color:#fff;}
  .homepage-container{min-height:100vh;padding:2rem;display:flex;flex-direction:column;}

  .dat-detail-wrap{
    padding:1rem;
    min-height:calc(100vh - 120px);
    background:
      radial-gradient(700px 500px at 20% 15%, var(--brand-top-20), transparent 60%),
      radial-gradient(600px 400px at 80% 85%, var(--brand-bot-40), transparent 60%),
      #000;
    color:#fff;
  }
  .dc-card{
    width:100%;
    background:#050008;
    border:1px solid rgba(255,255,255,.12);
    border-radius:14px;
    box-shadow:0 10px 30px rgba(0,0,0,.45);
    padding:14px;
    margin:0 auto 12px;
    max-width:980px;
  }
  .dc-note{ color:rgba(255,255,255,.85); padding:1rem; text-align:center; }

  .dc-btn{
    padding:.55rem .95rem;
    border:none;
    border-radius:10px;
    font-weight:800;
    cursor:pointer;
    background:var(--brand-grad);
    color:#fff;
    transition:transform .15s ease, box-shadow .15s ease, opacity .15s ease;
    box-shadow:0 4px 12px rgba(164,16,232,.25);
    display:inline-flex; align-items:center; justify-content:center;
  }
  .dc-btn:hover{ transform:translateY(-1px); box-shadow:0 6px 16px rgba(164,16,232,.35); }
  .dc-btn:active{ transform:translateY(0); }
  .dc-btn.small{ padding:.45rem .75rem; font-size:.9rem; }
  .dc-btn.full{ width:100%; }
  .dc-btn.ghost{
    background:transparent;
    color:#fff;
    border:1px solid rgba(255,255,255,.25);
    box-shadow:none;
  }

  .dat-header{
    display:flex; align-items:center; justify-content:space-between; gap:.75rem;
  }
  .dat-title{ flex:1; min-width:0; }
  .dat-song{
    font-size:1.2rem; font-weight:900;
    background:var(--brand-grad); -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
  }
  .dat-artist{ color:rgba(255,255,255,.75); font-size:.95rem; }

  .dat-hero{ display:flex; gap:1rem; align-items:stretch; }
  .dat-art{
    width:220px; height:220px; object-fit:cover; border-radius:12px;
    border:2px solid var(--brand-top);
    flex-shrink:0;
  }
  .dat-meta{
    display:grid; grid-template-columns:1fr 1fr; gap:.75rem; align-content:start; flex:1;
  }
  .meta-row{
    padding:.75rem .85rem; border-radius:10px;
    background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08);
  }
  .meta-label{ display:block; color:rgba(255,255,255,.7); font-size:.85rem; margin-bottom:.15rem; }
  .meta-value{ font-weight:900; background:var(--brand-grad); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }

  .list-title{
    font-weight:900; margin-bottom:.5rem;
    background:var(--brand-grad); -webkit-background-clip:text; -webkit-text-fill-color:transparent;
  }

  .ed-list{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:.6rem; }
  .ed-item{
    display:flex; align-items:center; justify-content:space-between; gap:1rem;
    padding:12px; border-radius:10px; background:#09020f; border:1px solid rgba(255,255,255,.08);
  }
  .ed-left{ display:flex; gap:1.25rem; flex-wrap:wrap; }
  .ed-line{ display:flex; gap:.35rem; align-items:baseline; }
  .ed-label{ color:rgba(255,255,255,.7); font-size:.9rem; }
  .ed-value{ font-weight:800; }

  .chip{
    padding:.35rem .65rem; border-radius:999px;
    border:1px solid rgba(255,255,255,.25); color:#fff; background:transparent;
    font-weight:700; font-size:.85rem; white-space:nowrap;
  }
  .chip.solid{
    background:rgba(254,36,173,.18); border-color:var(--brand-top);
  }

  /* modal */
  .modal-overlay{
    position:fixed; inset:0; background:rgba(0,0,0,.65);
    display:flex; align-items:center; justify-content:center; z-index:9999;
  }
  .modal{
    width:92%; max-width:460px; background:#09020f;
    border:1px solid rgba(255,255,255,.12); border-radius:14px;
    padding:16px; box-shadow:0 10px 30px rgba(0,0,0,.45);
  }
  .modal-title{
    font-size:1.05rem; font-weight:900; margin-bottom:.6rem;
    background:var(--brand-grad); -webkit-background-clip:text; -webkit-text-fill-color:transparent;
    text-align:center;
  }
  .form-row{ margin-bottom:.65rem; }
  .form-label{ display:block; color:rgba(255,255,255,.75); margin-bottom:.2rem; }
  .form-value{ font-weight:800; }
  .text-input{
    width:100%; padding:.6rem .7rem; border-radius:8px; border:1px solid rgba(255,255,255,.18);
    background:#0b0611; color:#fff; outline:none;
  }
  .error{ color:#ff6b6b; font-weight:700; margin:.35rem 0; }
  .modal-actions{ display:grid; grid-template-columns:1fr; gap:.5rem; margin-top:.6rem; }

  @media (max-width: 780px){
    .dat-hero{ flex-direction:column; }
    .dat-art{ width:100%; height:auto; max-height:360px; }
    .dat-meta{ grid-template-columns:1fr 1fr; }
  }
  @media (max-width: 520px){
    .dat-meta{ grid-template-columns:1fr; }
  }
  `}</style>
);
